import React, { useEffect, useState } from "react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { heroStats, heroImages } from "../data/mock";
import Picture from "./Picture";
import AwardBadges from "./AwardBadges";

const SLIDE_MS = 5000;

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Per-visit variety comes from the ADVANCE SEQUENCE (the order the carousel
// steps through the slides), NOT from the DOM order of the slide elements.
// The slides always render in a fixed order (heroImages), so the prerendered
// HTML and the client's first render match exactly — no hydration mismatch
// (React #418). The sequence is randomised AFTER mount, where it can't affect
// hydration.
//
// The sequence keeps index 0 (the preloaded LCP image) first and avoids two
// child photos back-to-back (including the wrap-around) so the homepage never
// reads as a "children's website". Since index 0 is an adult image the
// wrap-around edge is always safe. Retries up to 50 times; with only 2 children
// in 5 slides this nearly always succeeds first try.
const shuffleSequence = (images) => {
  const isChild = (idx) => images[idx]?.category === "child";
  const rest = images.map((_, i) => i).slice(1); // indices 1..n-1
  const valid = (seq) => {
    for (let i = 0; i < seq.length; i++) {
      if (isChild(seq[i]) && isChild(seq[(i + 1) % seq.length])) return false;
    }
    return true;
  };
  for (let attempt = 0; attempt < 50; attempt++) {
    const seq = [0, ...shuffle(rest)];
    if (valid(seq)) return seq;
  }
  return [0, ...shuffle(rest)]; // give up after 50 tries (statistically impossible)
};

const Hero = () => {
  // Slides always render in a fixed DOM order (heroImages) so the prerendered
  // HTML and the client's first render match — no hydration mismatch. Index 0
  // is the preloaded LCP image. `seq` is the order the carousel advances
  // through; `step` is the current position, so the visible slide is seq[step].
  const slides = heroImages;
  const [seq, setSeq] = useState(() => slides.map((_, i) => i)); // identity until mount
  const [step, setStep] = useState(0);
  const active = seq[step];
  // All five slides stack in the same spot INSIDE the viewport, so
  // loading="lazy" can't defer them - the browser fetches every hero AVIF
  // (~0.5 MB) up front, competing with the LCP image. Load only the first
  // slide for the initial paint (it's the LCP element) and reveal the rest
  // once the carousel first advances (5s) or the visitor interacts - i.e.
  // after LCP. Prerender snapshots well before the 5s tick, so the static
  // HTML ships with just the first slide's <img>.
  const [revealed, setRevealed] = useState(false);
  const reveal = () => setRevealed(true);

  // Randomise the advance order AFTER hydration (index 0 stays first, no two
  // child photos adjacent). Post-mount, so it never causes a hydration
  // mismatch; the hidden slides reorder invisibly.
  useEffect(() => {
    setSeq(shuffleSequence(slides));
  }, [slides]);

  // Auto-advance the carousel. Reveals the other slides on the first tick (5s),
  // after the initial paint. Skipped under prefers-reduced-motion.
  useEffect(() => {
    if (slides.length <= 1) return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      setStep((s) => (s + 1) % slides.length);
      setRevealed(true);
    }, SLIDE_MS);
    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <section className="bs-hero" id="home">
      <div className="bs-container bs-hero__grid">
        <div className="bs-hero__copy">
          <span className="bs-eyebrow">
            <ShieldCheck size={14} />
            {/* Shorter eyebrow on phones (it otherwise wraps to two lines);
                the fuller strapline stays on wider screens. CSS swaps them. */}
            <span className="bs-eyebrow__wide">Learn and practise British Sign Language</span>
            <span className="bs-eyebrow__narrow">Learn British Sign Language online</span>
          </span>

          <h1 className="bs-hero__title">
            Learn{" "}
            <span className="bs-hero__accent">British Sign Language</span>{" "}
            online at your own pace
          </h1>

          <p className="bs-hero__lede">
            Work through our online course, or use hundreds of free BSL signs,
            games, tools and printable resources.
          </p>

          <div className="bs-hero__cta">
            <a
              href="/enrol#start"
              className="bs-btn bs-btn--solid"
              data-testid="hero-start-course-btn"
              data-analytics="hero-start-course"
            >
              Start the course
              <ArrowRight size={18} />
            </a>
            <a
              href="#bsl-widgets-hub-section"
              className="bs-hero__quiet-link bs-hero__quiet-link--strong"
              data-testid="hero-explore-free-link"
              data-analytics="hero-explore-free"
              onClick={(e) => {
                e.preventDefault();
                const ribbon = document.getElementById("bs-hero-ribbon");
                if (ribbon) ribbon.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              See our free BSL resources
              <ArrowRight size={14} />
            </a>
          </div>

          <a
            href="/what-is-bsl"
            className="bs-hero__quiet-link bs-hero__quiet-link--muted"
            data-testid="hero-what-is-bsl-link"
          >
            What is BSL?
          </a>

          <dl className="bs-hero__stats">
            {heroStats.map((s) => (
              <div className="bs-hero__stat" key={s.label}>
                <dt>{s.value}</dt>
                <dd>{s.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="bs-hero__visual">
          <div
            className="bs-hero__photo bs-hero__photo--carousel"
            data-testid="hero-carousel"
            role="group"
            aria-roledescription="carousel"
            aria-label="British Sign Language in everyday life"
            onPointerEnter={reveal}
          >
            {slides.map((img, i) => (
              <Picture
                key={img.src}
                src={i === 0 || revealed ? img.src : undefined}
                alt={img.alt}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : "auto"}
                style={img.position ? { objectPosition: img.position } : undefined}
                className={`bs-hero__slide${i === active ? " is-active" : ""}`}
                aria-hidden={i === active ? "false" : "true"}
              />
            ))}

            <div className="bs-hero__dots" role="tablist" aria-label="Choose hero image">
              {slides.map((img, i) => (
                <button
                  key={img.src}
                  type="button"
                  role="tab"
                  aria-selected={i === active}
                  aria-label={`Show image ${i + 1} of ${slides.length}`}
                  className={`bs-hero__dot${i === active ? " is-active" : ""}`}
                  onClick={() => { setStep(seq.indexOf(i)); reveal(); }}
                  data-testid={`hero-dot-${i}`}
                />
              ))}
            </div>
          </div>
          <AwardBadges variant="hero" />
        </div>

      </div>
    </section>
  );
};

export default Hero;
